import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function NewTender() {
  const navigate = useNavigate();
  const { user, org } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "", tags: "",
    budget_value: 0, due_date: "",
  });

  // Only owner/department_admin can post tenders (PRD §4) — contributors excluded,
  // unlike the intra-org CreatorRoute which allows all three roles.
  if (!["owner", "department_admin"].includes(user?.role) || !org?.is_premium) {
    return <Navigate to="/app/marketplace" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        budget_value: Number(form.budget_value) || 0,
        due_date: form.due_date || null,
      };
      const { data } = await api.post("/marketplace/tenders", payload);
      toast.success("Tender posted to the marketplace");
      navigate(`/app/marketplace/tenders/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">New Tender</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2">Post to the marketplace</h1>
        <p className="text-slate-600 mt-1">Other organizations will be able to see and bid on this.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-8">
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider">Title</Label>
              <Input required value={form.title} data-testid="tender-title"
                onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-12 text-base" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium uppercase tracking-wider">Description</Label>
              <Textarea required rows={5} value={form.description} data-testid="tender-description"
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Category / domain</Label>
                <Input required value={form.category} data-testid="tender-category"
                  placeholder="e.g. Compliance, Legal, Logistics"
                  onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Tags (comma separated)</Label>
                <Input value={form.tags} data-testid="tender-tags"
                  onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Budget (informational)</Label>
                <Input type="number" min="0" value={form.budget_value} data-testid="tender-budget"
                  onChange={(e) => setForm({ ...form, budget_value: e.target.value })} />
                <p className="text-xs text-slate-500">No payment is processed on the platform yet — this is shown to bidders as context only.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Due date</Label>
                <Input type="date" value={form.due_date} data-testid="tender-duedate"
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="tender-submit" className="bg-blue-600 hover:bg-blue-700">
                {loading ? "Posting…" : "Post tender"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
